"use client";
import { useRouter } from "next/navigation";
import Link from "next/link";
import api from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import { useState, useEffect } from "react";
import LikeCard from "@/components/LikeCard";
import LikesNavbar from "@/components/LikesNavbar";
import Modal from "@/components/Modal";

export default function LikesPage() {
  const { isAuthenticated } = useAuthStore();
  const router = useRouter();

  useEffect(() => {
    if (!isAuthenticated) {
      router.push("/auth");
    }
  }, [isAuthenticated, router]);

  const [activeTab, setActiveTab] = useState("narrators"); // "narrators" | "likes"

  // --- Narrators tab state ---
  const [narrators, setNarrators] = useState([]);
  const [narratorsLoading, setNarratorsLoading] = useState(false);

  // --- Likes tab state ---
  const [searchTerm, setSearchTerm] = useState("");
  const [stories, setStories] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folderFilter, setFolderFilter] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedLike, setSelectedLike] = useState(0);
  const [isModalOpen, setModalOpen] = useState(false);
  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const fetchNarrators = async () => {
    setNarratorsLoading(true);
    try {
      const res = await api("/api/v1/creators/narrators", { method: "GET" });
      if (res.status === "ok") {
        setNarrators(res.data);
      }
    } catch (e) {
      console.log(e);
    }
    setNarratorsLoading(false);
  };

  const fetchLikes = async () => {
    setLoading(true);
    let requestUrl = `/api/v1/likes?folder=${folderFilter}`;
    if (searchTerm !== "") requestUrl += `&search=${searchTerm}`;
    try {
      const res = await api(requestUrl, { method: "GET" });
      if (res.status === "ok") {
        setStories(res.likes);
        setFolders(res.folders);
      }
    } catch (e) {
      console.log(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchNarrators();
  }, []);

  useEffect(() => {
    if (activeTab === "likes") {
      fetchLikes();
    }
  }, [activeTab, searchTerm, folderFilter]);

  return (
    <>
      <LikesNavbar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        folders={folders}
        folderFilter={folderFilter}
        setFolderFilter={setFolderFilter}
      />

      {/* Narrators tab */}
      {activeTab === "narrators" && (
        <>
          <div className="mt-8">
          {narratorsLoading ? (
            <div className="w-max mx-auto mt-3">
              <svg
                aria-hidden="true"
                className="w-8 h-8 text-gray-200 animate-spin fill-c3"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 50.5908 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            </div>
          ) : (
            narrators.length > 0 && (
                  <div className="grid grid-cols-3 gap-x-4 gap-y-6 px-4 md:mx-auto md:max-w-xl">
                {narrators.map((narrator) => (
                  <Link
                    key={narrator.id}
                    href='#'
                    className="group flex min-w-0 flex-col items-center gap-2"
                  >
                    <div
                      className="
                        w-full md:max-w-28 rounded-full p-0.5
                        bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600
                        group-hover:bg-none group-hover:bg-c3
                        group-focus:bg-none group-focus:bg-c3
                        transition-all duration-200 shadow-md
                      "
                    >
                      <div className="relative w-full aspect-square rounded-full overflow-hidden bg-black">
                        <img
                          src={
                            narrator.profile_photo
                              ? process.env.NEXT_PUBLIC_ASSETS_URL +
                              narrator.profile_photo
                              : "/images/no-image.jpeg"
                          }
                          alt={narrator.alias}
                          className="absolute inset-0 block w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    <span
                      className="
                        w-full text-md text-center text-gray-200
                        line-clamp-1
                        group-hover:text-c3 transition-colors
                      "
                    >
                      {narrator.alias}
                    </span>
                  </Link>
                ))}
              </div>
            )
          )}
        </div>
        </>
      )}

      {/* Likes tab */}
      {activeTab === "likes" && (
        <>
          <Modal
            fetchData={fetchLikes}
            isOpen={isModalOpen}
            onClose={closeModal}
            folders={folders}
            like={selectedLike}
          />
          <div className="grid grid-cols-2 gap-3 p-3 md:max-w-xl md:mx-auto">
            {stories.map((s) => (
              <LikeCard
                fetchData={fetchLikes}
                key={s.id}
                data={s}
                setSelectedLike={setSelectedLike}
                openModal={openModal}
                folderFilter={folderFilter}
              />
            ))}
          </div>
          {loading && (
            <div className="w-max mx-auto mt-3">
              <svg
                aria-hidden="true"
                className="w-8 h-8 text-gray-200 animate-spin fill-c3"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 50.5908 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            </div>
          )}
        </>
      )}
    </>
  );
}
